import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { invalidateTaskQueries } from "@/lib/task-query-utils";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { X, MessageSquare, GripVertical, Heart } from "lucide-react";
import { useFollowedAnimalIds } from "@/hooks/use-followed-animals";
import { AnimalAvatar } from "@/components/animals/animal-avatar";
import { InteractionDetailDrawer } from "@/components/interactions/InteractionDetailDrawer";
import {
  DndContext,
  closestCenter,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
  type DragStartEvent,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import type { TaskInteractionLink } from "./task-types";

interface InteractionSearchResult {
  id: string;
  animalId: string;
  eventType: string;
  occurredAt: string;
  author: string | null;
  scoreCode: string | null;
  locationCode: string | null;
  animalName: string | null;
  animalSpecies: string | null;
}

interface TaskInteractionLinkerProps {
  taskId: string;
  interactions: TaskInteractionLink[];
  canEdit: boolean;
  interactionSearchQuery: string;
  setInteractionSearchQuery: (q: string) => void;
  interactionSearchResults: InteractionSearchResult[] | undefined;
}

function InteractionItemContent({
  interaction,
  canEdit,
  onOpen,
  onUnlink,
  dragHandle,
  isDragOverlay,
  followedIds,
}: {
  interaction: TaskInteractionLink;
  canEdit: boolean;
  onOpen: (i: TaskInteractionLink) => void;
  onUnlink: (id: string) => void;
  dragHandle?: React.ReactNode;
  isDragOverlay?: boolean;
  followedIds: string[];
}) {
  return (
    <div
      className={`flex items-start justify-between gap-1 py-1 ${isDragOverlay ? "rounded-md border border-border bg-background shadow-md px-1" : ""}`}
      data-testid={`task-interaction-link-${interaction.id}`}
    >
      <div className="flex items-start gap-1 min-w-0">
        {dragHandle}
        <button
          className="flex items-start gap-2 text-left hover:underline min-w-0"
          onClick={() => onOpen(interaction)}
          data-testid={`task-button-open-interaction-${interaction.id}`}
        >
          <AnimalAvatar
            animalId={interaction.animalId || ""}
            photoUrl={interaction.animalPhotoUrl ?? null}
            name={interaction.animalName || ""}
            species={interaction.animalSpecies || ""}
            className="h-5 w-5 shrink-0"
          />
          <span className="text-sm break-words">
            <span>{interaction.animalName || interaction.animalId || "Interaction"}</span>
            {interaction.animalId && followedIds.includes(interaction.animalId) && <Heart className="h-3 w-3 fill-rose-500 text-rose-500 inline-block align-middle ml-0.5" data-testid={`heart-followed-linked-${interaction.animalId}`} />}
            {interaction.animalSpecies && <Badge variant="outline" className="text-xs ml-1" data-testid={`badge-species-interaction-${interaction.id}`}>{interaction.animalSpecies.toUpperCase()}</Badge>}
            {interaction.animalName && interaction.animalId && <span className="text-muted-foreground ml-1">({interaction.animalId})</span>}
            {interaction.eventType && <span className="text-muted-foreground ml-1">· {interaction.eventType}{interaction.occurredAt && ` on ${new Date(interaction.occurredAt).toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "numeric" })}`}</span>}
            {!interaction.eventType && interaction.occurredAt && <span className="text-muted-foreground ml-1">· {new Date(interaction.occurredAt).toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "numeric" })}</span>}
            {interaction.scoreCode && <Badge variant="outline" className="text-xs ml-1">{interaction.scoreCode}</Badge>}
            {interaction.locationCode && <> <Badge variant="outline" className="text-xs">{interaction.locationCode}</Badge></>}
            {interaction.author && <> <Badge variant="outline" className="text-xs">{interaction.author}</Badge></>}
          </span>
        </button>
      </div>
      {canEdit && (
        <Button variant="ghost" size="icon" onClick={() => onUnlink(interaction.id)} data-testid={`task-button-unlink-interaction-${interaction.id}`}>
          <X className="w-3 h-3" />
        </Button>
      )}
    </div>
  );
}

function SortableInteractionItem({
  interaction,
  onOpen,
  onUnlink,
  followedIds,
}: {
  interaction: TaskInteractionLink;
  onOpen: (i: TaskInteractionLink) => void;
  onUnlink: (id: string) => void;
  followedIds: string[];
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: interaction.id });
  const style = { transform: CSS.Transform.toString(transform), transition };

  if (isDragging) {
    return (
      <div ref={setNodeRef} style={style} className="rounded-md border-2 border-dashed border-muted-foreground/30 bg-muted/30 py-3 my-0.5" />
    );
  }

  return (
    <div ref={setNodeRef} style={style}>
      <InteractionItemContent
        interaction={interaction}
        canEdit={true}
        onOpen={onOpen}
        onUnlink={onUnlink}
        followedIds={followedIds}
        dragHandle={
          <span {...attributes} {...listeners} className="cursor-grab text-muted-foreground shrink-0 mt-0.5">
            <GripVertical className="w-3 h-3" />
          </span>
        }
      />
    </div>
  );
}

export function TaskInteractionLinker({
  taskId,
  interactions,
  canEdit,
  interactionSearchQuery,
  setInteractionSearchQuery,
  interactionSearchResults,
}: TaskInteractionLinkerProps) {
  const { toast } = useToast();
  const { data: followsData } = useFollowedAnimalIds();
  const followedIds = followsData?.animalIds ?? [];
  const [interactionDrawerOpen, setInteractionDrawerOpen] = useState(false);
  const [selectedInteraction, setSelectedInteraction] = useState<any>(null);
  const [activeId, setActiveId] = useState<string | null>(null);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const linkInteractionMutation = useMutation({
    mutationFn: async (interactionId: string) => apiRequest("POST", `/api/tasks/${taskId}/interactions`, { interactionId }),
    onSuccess: () => { invalidateTaskQueries(); setInteractionSearchQuery(""); },
    onError: (error: Error) => toast({ title: "Error", description: error.message, variant: "destructive" }),
  });

  const unlinkInteractionMutation = useMutation({
    mutationFn: async (linkId: string) => apiRequest("DELETE", `/api/tasks/${taskId}/interactions/${linkId}`),
    onSuccess: () => invalidateTaskQueries(),
    onError: (error: Error) => toast({ title: "Error", description: error.message, variant: "destructive" }),
  });

  const reorderMutation = useMutation({
    mutationFn: async (orderedIds: string[]) => apiRequest("POST", `/api/tasks/${taskId}/interactions/reorder`, { orderedIds }),
    onError: (error: Error) => {
      invalidateTaskQueries();
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  function handleDragStart(event: DragStartEvent) {
    setActiveId(event.active.id as string);
  }

  function handleDragEnd(event: DragEndEvent) {
    setActiveId(null);
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = interactions.findIndex(i => i.id === active.id);
    const newIndex = interactions.findIndex(i => i.id === over.id);
    const reordered = arrayMove(interactions, oldIndex, newIndex);

    const cached = queryClient.getQueryData<{ task: any }>(["/api/tasks", taskId]);
    if (cached) {
      queryClient.setQueryData(["/api/tasks", taskId], {
        ...cached,
        task: { ...cached.task, interactions: reordered },
      });
    }

    reorderMutation.mutate(reordered.map(i => i.id));
  }

  async function handleOpenInteraction(i: TaskInteractionLink) {
    try {
      const full = await apiRequest("GET", `/api/interactions/${i.interactionId}`) as any;
      setSelectedInteraction(full);
      setInteractionDrawerOpen(true);
    } catch {
      toast({ title: "Could not load interaction details", variant: "destructive" });
    }
  }

  const filteredResults = interactionSearchResults?.filter(
    i => !interactions.some(linked => linked.interactionId === i.id)
  );

  const activeInteraction = activeId ? interactions.find(i => i.id === activeId) : null;

  return (
    <>
      <div className="space-y-2">
        <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-1">
          <MessageSquare className="w-3 h-3" /> Interactions
          <Badge variant="secondary" className="text-[10px] px-1.5 py-0 ml-1" data-testid="badge-interactions-count">
            {interactions.length}
          </Badge>
        </Label>
        {canEdit && (
          <div className="space-y-1">
            <Input
              placeholder="Search interactions to add..."
              value={interactionSearchQuery}
              onChange={e => setInteractionSearchQuery(e.target.value)}
              className="h-8 text-sm"
              data-testid="task-input-search-interaction"
            />
            {filteredResults && filteredResults.length > 0 && (
              <div className="border rounded-md max-h-32 overflow-y-auto list-zebra shadow-sm">
                {filteredResults.map(i => (
                  <button
                    key={i.id}
                    className="w-full text-left px-2 py-1 text-sm hover-elevate"
                    onClick={() => linkInteractionMutation.mutate(i.id)}
                    data-testid={`task-button-link-interaction-${i.id}`}
                  >
                    <span>{i.animalName || i.animalId}</span>
                    {i.animalId && followedIds.includes(i.animalId) && <Heart className="h-3 w-3 fill-rose-500 text-rose-500 inline-block align-middle ml-0.5" data-testid={`heart-followed-${i.animalId}`} />}
                    {i.animalName && <span className="text-muted-foreground ml-1">({i.animalId})</span>}
                    <span className="text-muted-foreground ml-1">· {i.eventType}{i.occurredAt && ` on ${new Date(i.occurredAt).toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "numeric" })}`}</span>
                    {i.scoreCode && <Badge variant="outline" className="text-xs ml-1">{i.scoreCode}</Badge>}
                    {i.locationCode && <> <Badge variant="outline" className="text-xs">{i.locationCode}</Badge></>}
                    {i.author && <> <Badge variant="outline" className="text-xs">{i.author}</Badge></>}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
        <div className="list-zebra">
          {canEdit && interactions.length > 1 ? (
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
              <SortableContext items={interactions.map(i => i.id)} strategy={verticalListSortingStrategy}>
                {interactions.map(i => (
                  <SortableInteractionItem
                    key={i.id}
                    interaction={i}
                    onOpen={handleOpenInteraction}
                    onUnlink={(id) => unlinkInteractionMutation.mutate(id)}
                    followedIds={followedIds}
                  />
                ))}
              </SortableContext>
              <DragOverlay>
                {activeInteraction && (
                  <InteractionItemContent
                    interaction={activeInteraction}
                    canEdit={false}
                    onOpen={() => {}}
                    onUnlink={() => {}}
                    followedIds={followedIds}
                    isDragOverlay
                    dragHandle={
                      <span className="cursor-grabbing text-muted-foreground shrink-0 mt-0.5">
                        <GripVertical className="w-3 h-3" />
                      </span>
                    }
                  />
                )}
              </DragOverlay>
            </DndContext>
          ) : (
            interactions.map(i => (
              <InteractionItemContent
                key={i.id}
                interaction={i}
                canEdit={canEdit}
                onOpen={handleOpenInteraction}
                onUnlink={(id) => unlinkInteractionMutation.mutate(id)}
                followedIds={followedIds}
              />
            ))
          )}
        </div>
      </div>

      {selectedInteraction && (
        <InteractionDetailDrawer
          open={interactionDrawerOpen}
          onOpenChange={(open) => {
            setInteractionDrawerOpen(open);
            if (!open) setSelectedInteraction(null);
          }}
          interaction={selectedInteraction}
          animalId={selectedInteraction.animalId}
        />
      )}
    </>
  );
}
